import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';

import { FormContainer, FormWrap } from '../FormContainer/FormContainer';
import useStepPost from '../../hooks/useStepPost';
import useLegalEntityEinDispatch from '../../hooks/useLegalEntityEinDispatch';
import Checkbox from '../Fields/Checkbox';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import InputText from '../Fields/InputText';
import { IconButton } from '../IconButton/IconButton';
import { ReactComponent as EyeOn } from '../../assets/images/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../assets/images/svg/eye-off.svg';
import {
  extractValue,
  hashedMask,
  isPositiveInteger,
} from '../../util/helpers/einHelper';
import { MaskedFieldContainer } from '../Fields/MaskedField';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
} from '../../constants/sessionStorage';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';

const HiddenField = styled.div`
  opacity: 0;
  cursor: 'context-menu';
  width: 0;
  height: 0;
`;

const BusinessEinForm = ({
  applicantType,
  nextPath,
  onModalClose,
  formId,
  setEventType,
  eventType,
}) => {
  const { t } = useTranslation();
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const { legalEntityEinDispatch } = useLegalEntityEinDispatch();
  const [actualEin, setActualEin] = useState('');
  const [maskedEin, setMaskedEin] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const ApplicantData = useApplicantData(applicantType);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Business ein number',
      accountType: accountGtmType,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Business ein number',
      accountType: accountGtmType,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const defaultValues = {
    Ein:
      getApplicantData(ApplicantData, 'EIN') === null
        ? ''
        : getApplicantData(ApplicantData, 'EIN'),
    NoEin: true,
  };

  const { handleSubmit, control, setError, clearErrors } = useForm({
    defaultValues,
  });

  useEffect(() => {
    const val = getApplicantData(ApplicantData, 'EIN');
    if (val !== null) {
      setActualEin(extractValue(val));
      setMaskedEin(hashedMask(extractValue(val)));
    }
  }, [ApplicantData]);

  const onSubmit = ({ NoEin }) => {
    if (isSalesAssisted) {
      legalEntityEinDispatch({
        data: {
          actualEin,
          NoEin,
        },
        defaultValues,
        onSuccess:
          eventType === 'Form'
            ? () => onSuccess()
            : () => {
                setHasError(false);
                setSaveAndCloseModalOpen(true);
              },
        onError:
          eventType === 'Form'
            ? () => {}
            : () => {
                setHasError(true);
                setSaveAndCloseModalOpen(true);
              },
      });
      handleGtmEvent();
    }
    const Ein = `${actualEin.slice(0, 2)}-${actualEin.slice(2, 9)}`;

    if (isEinFieldDisabled) {
      clearErrors();
    } else {
      if (Ein.length === 1) {
        setError('Ein', {
          type: 'manual',
          message: t('fields.errors.validation.einRequired'),
        });
        return;
      }
      if (Ein.length < 10) {
        setError('Ein', {
          type: 'manual',
          message: t('fields.errors.validation.einNotValid'),
        });
        return;
      }
      if (
        !isPositiveInteger(Ein.slice(0, 2)) ||
        !isPositiveInteger(Ein.slice(3, 10))
      ) {
        setError('Ein', {
          type: 'manual',
          message: t('fields.errors.validation.einDigits'),
        });
        return;
      }
    }

    legalEntityEinDispatch({
      data: {
        Ein,
        NoEin,
      },
      defaultValues,
      onSuccess:
        eventType === 'Form'
          ? () => onSuccess()
          : () => {
              setHasError(false);
              setSaveAndCloseModalOpen(true);
            },
      onError:
        eventType === 'Form'
          ? () => {}
          : () => {
              setHasError(true);
              setSaveAndCloseModalOpen(true);
            },
    });
    handleGtmEvent();
  };

  const handleChange = (event) => {
    const prevValue = maskedEin;
    const newValue = event.target.value;
    let actual = actualEin;

    if (newValue.length > 10) {
      return;
    }

    if (newValue.length > prevValue.length) {
      const newChar = newValue.split('').pop();
      actual = `${actual}${newChar}`;
    } else {
      const charsRemovedCount = prevValue.length - newValue.length;
      actual = actual.substr(0, actual.length - charsRemovedCount);
    }

    setActualEin(actual);
    setMaskedEin(hashedMask(actual, isVisible));
  };
  useEffect(() => {
    setMaskedEin(hashedMask(actualEin, isVisible));
  }, [actualEin, isVisible]);

  const isEinFieldDisabled = false;

  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <HiddenField>
            <Checkbox
              label={t('verifyBusinessEin.noEin')}
              onClick={() => {
                onChange(!value);
              }}
              value={1}
            />
          </HiddenField>
        )}
        name="NoEin"
      />
      <FormContainer mb={22}>
        <Controller
          control={control}
          render={({ field: { onChange, value, ...field }, fieldState }) => (
            <MaskedFieldContainer>
              <InputText
                label={t('verifyBusinessEin.ein')}
                {...field}
                {...fieldState}
                placeholder="XX-XXXXXXX"
                onChange={handleChange}
                value={maskedEin}
                disabled={isEinFieldDisabled}
                autocomplete="off"
                autoFocus
              />
              <IconButton
                type="button"
                title={`${isVisible ? 'Hide EIN' : 'Show EIN'}`}
                onClick={() => setIsVisible(!isVisible)}
              >
                {isVisible ? <EyeOn /> : <EyeOff />}
              </IconButton>
            </MaskedFieldContainer>
          )}
          name="Ein"
        />
      </FormContainer>
      <OnBoardingCta
        type="submit"
        title={t('continue')}
        onClick={() => setEventType('Form')}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

BusinessEinForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default BusinessEinForm;
